import {
  type PayloadAction,
  createSelector,
  createSlice,
} from '@reduxjs/toolkit';
import { groupBy } from 'lodash';
import type { TRootState } from 'slices/store';
import { createAppAsyncThunk } from 'slices/utils';
import type {
  IDefaultAPIResult,
  IDefaultAPIState,
} from 'types/state.interface';
import type { WithOptional } from 'types/utils.type';
import {
  addEntityToState,
  deleteEntityFromState,
  finishedLoadingFailure,
  finishedLoadingSuccess,
  isLoadingRequest,
  prepEntityForAPICall,
  stateHasFetchedAllObjectsSuccess,
  upsertEntity,
} from 'utils/sliceHelpers';
import {
  type TRolePermission,
  type TRolePermissionGroup,
  createRolePermission as create,
  createRolePermissionGroup,
} from '@timeedit/types';
import api from '../services/api.service';

type TRolePermissionsState = IDefaultAPIState<TRolePermission> & {
  groups: TRolePermissionGroup[];
};

export const initialState: TRolePermissionsState = {
  loading: false,
  hasErrors: false,
  results: [],
  groups: [],
  map: {},
  page: 0,
  limit: 10000,
  totalPages: 0,
};

// Slice
const slice = createSlice({
  name: 'rolePermissions',
  initialState,
  reducers: {
    fetchRolePermissionsRequest: (state) => {
      isLoadingRequest(state);
    },
    fetchRolePermissionsSuccess: (
      state,
      { payload }: PayloadAction<IDefaultAPIResult>
    ) => {
      stateHasFetchedAllObjectsSuccess(payload, state, create);
      finishedLoadingSuccess(state);
    },
    fetchRolePermissionsFailure: (state) => {
      finishedLoadingFailure(state);
    },
    fetchRolePermissionGroupsSuccess: (
      state,
      {
        payload,
      }: PayloadAction<
        IDefaultAPIResult<WithOptional<TRolePermissionGroup, 'groupActions'>>
      >
    ) => {
      state.groups = (payload?.results || []).map((obj) =>
        createRolePermissionGroup(obj)
      );
      finishedLoadingSuccess(state);
    },
    createRolePermission: (state) => {
      addEntityToState(state, { id: 0, name: 'New role permission' }, create);
      finishedLoadingSuccess(state);
    },
    saveRolePermissionRequest: (state) => {
      isLoadingRequest(state);
    },
    saveRolePermissionSuccess: (
      state,
      { payload }: PayloadAction<WithOptional<TRolePermission, 'groupActions'>>
    ) => {
      upsertEntity<
        WithOptional<TRolePermission, 'groupActions'>,
        TRolePermission
      >(state, payload, create);
      finishedLoadingSuccess(state);
    },
    deleteRolePermissionRequest: (state) => {
      isLoadingRequest(state);
    },
    deleteRolePermissionSuccess: (
      state,
      { payload: rolePermissionId }: PayloadAction<number>
    ) => {
      deleteEntityFromState(rolePermissionId, state);
      finishedLoadingSuccess(state);
    },
    deleteRolePermissionFailure: (state) => {
      finishedLoadingFailure(state);
    },
  },
});

export default slice.reducer;

// Selectors
export const rolePermissionsLoading = (state: TRootState) =>
  state.rolePermissions.loading;
export const rolePermissionsSelector = (state: TRootState) =>
  state.rolePermissions.results;
export const rolePermissionSelector =
  (id?: string | number) => (state: TRootState) =>
    id != null ? state.rolePermissions.map[id] : undefined;
export const rolePermissionGroupsSelector = (state: TRootState) =>
  state.rolePermissions.groups;

export const selectAllowedActionsForGroup = createSelector(
  [rolePermissionGroupsSelector, (_: TRootState, groupId: string) => groupId],
  (groups, groupId) => {
    const group = groups.find((group) => group.id === groupId);
    if (!group) return [];
    return group.groupActions;
  }
);

const selectRolePermissionGroups = (state: TRootState) =>
  state.rolePermissions.groups;

const selectRolePermissions = (state: TRootState) =>
  state.rolePermissions.results;

export const selectRolePermissionsAsTreeData = createSelector(
  [selectRolePermissionGroups, selectRolePermissions],
  (groups = [], rolePermissions = []) => {
    // Group role permissions by group
    const groupedRolePermissions = groupBy(
      rolePermissions,
      'rolePermissionGroup'
    );

    // Create the tree
    const tree = groups.map((group) => {
      const rolePermissionsForGroup = (
        groupedRolePermissions[group.id] || []
      ).map((rolePermission) => ({
        title: rolePermission.name,
        key: rolePermission.id,
        children: [],
        isLeaf: true,
      }));

      return {
        title: group.id,
        key: group.id,
        children: rolePermissionsForGroup,
        selectable: false,
      };
    });

    // Handle role permissions without a group
    const unassigned = groupedRolePermissions.undefined;
    if (unassigned && unassigned.length) {
      const noGroup = {
        title: 'UNASSIGNED',
        key: 'UNASSIGNED',
        selectable: false,
        children: unassigned.map((rolePermission) => ({
          title: rolePermission.name,
          key: rolePermission.id,
          children: [],
          isLeaf: true,
        })),
      };
      tree.unshift(noGroup);
    }

    return tree;
  }
);

// Actions
export const {
  fetchRolePermissionsRequest,
  fetchRolePermissionsSuccess,
  fetchRolePermissionsFailure,
  fetchRolePermissionGroupsSuccess,
  createRolePermission,
  deleteRolePermissionRequest,
  deleteRolePermissionFailure,
  deleteRolePermissionSuccess,
  saveRolePermissionRequest,
  saveRolePermissionSuccess,
} = slice.actions;

export const fetchRolePermissions = createAppAsyncThunk(
  'rolePermissions/fetchRolePermissions',
  async (_, { dispatch }) => {
    try {
      dispatch(fetchRolePermissionsRequest());
      const result = await api.get({
        endpoint: `/role-permissions`,
      });
      dispatch(fetchRolePermissionsSuccess(result));
    } catch (e) {
      dispatch(fetchRolePermissionsFailure());
      console.error(e);
    }
  }
);

export const fetchRolePermissionGroups = createAppAsyncThunk(
  'rolePermissions/fetchRolePermissionGroups',
  async (_, { dispatch }) => {
    try {
      dispatch(fetchRolePermissionsRequest());
      const result = await api.get({
        endpoint: `/role-permissions/groups`,
      });
      dispatch(fetchRolePermissionGroupsSuccess(result));
    } catch (e) {
      dispatch(fetchRolePermissionsFailure());
      console.error(e);
    }
  }
);

export const saveRolePermission = createAppAsyncThunk<void, TRolePermission>(
  'rolePermissions/saveRolePermission',
  async (rolePermission, { dispatch }) => {
    try {
      dispatch(saveRolePermissionRequest());
      /**
       * Could be either create or update
       * Depending on which, we need to use different methods (POST or PATCH)
       */
      if (rolePermission.id === 0) {
        // Create a new one
        const preppedRolePermission = prepEntityForAPICall(
          rolePermission,
          true,
          true
        );
        const savedRolePermission = await api.post({
          endpoint: `/role-permissions`,
          data: preppedRolePermission,
        });
        dispatch(saveRolePermissionSuccess(savedRolePermission));
        dispatch(deleteRolePermissionSuccess(0));
      } else {
        const preppedRolePermission = prepEntityForAPICall(
          rolePermission,
          true,
          true
        );
        const updatedRolePermission = await api.patch({
          endpoint: `/role-permissions/${rolePermission.id}`,
          data: preppedRolePermission,
        });
        dispatch(saveRolePermissionSuccess(updatedRolePermission));
      }
    } catch (error) {
      console.error(error);
    }
  }
);

export const deleteRolePermission = createAppAsyncThunk<void, number>(
  'rolePermissions/deleteRolePermission',
  async (rolePermissionId, { dispatch }) => {
    try {
      dispatch(deleteRolePermissionRequest());
      /**
       * No need to send the request to the backend if the entity hasn't been saved
       */
      if (rolePermissionId !== 0)
        await api.delete({
          endpoint: `/role-permissions/${rolePermissionId}`,
        });
      dispatch(deleteRolePermissionSuccess(rolePermissionId));
    } catch (e) {
      dispatch(deleteRolePermissionFailure());
      console.error(e);
    }
  }
);
